import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  ClipboardList,
  Bookmark,
  AlertTriangle,
  Check,
  ExternalLink,
  Inbox,
  CircleUserRound,
  Trophy,
} from 'lucide-react';
import QuizrrLogo from '../../public/Images/QuizrrLogo.png';
import { cn } from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { DarkModeToggle } from './DarkModeToggle';
import { PresentationChartIcon } from '@phosphor-icons/react';
import hotkeys from 'hotkeys-js';
import axios from 'axios';
import JeeMainPaywallSheet from '../Challenge99/JeeMainPaywallSheet';
import { hasActiveJeeMainPack, canAccessChallenge } from '../Challenge99/jeeMainPaywall';

// Module-level, not component state, so it survives a Sidebar remount (see the comment at its one
// read site below) - starts null (unknown) and is filled in by the first successful fetch.
let cachedPlatformSettings = null;

const getStoredUserEmail = () => {
  try {
    return JSON.parse(localStorage.getItem('userProfile'))?.email ?? null;
  } catch {
    return null;
  }
};

export default function Sidebar({
  withOldComponent = false,
  purchasedPacks,
  purchasedFreePacksCollective,
  selectedPack,
  onPackChange,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const packId = localStorage.getItem('selectedPackId');
  const [showJeeMainPaywall, setShowJeeMainPaywall] = useState(false);
  // Every page owns its own <AppLayout>/<Sidebar> instance rather than sharing one across a layout
  // route, so switching e.g. pack-analysis -> mistakes fully remounts this component. Seeding state
  // from the cached value (instead of null) means a user who's already had platformSettings resolve
  // once this session doesn't see the nav flicker on every subsequent page switch while it refetches.
  const [platformSettings, setPlatformSettings] = useState(cachedPlatformSettings);

  useEffect(() => {
    axios
      .get('/api/platform-settings')
      .then((res) => {
        cachedPlatformSettings = res?.data?.data ?? null;
        setPlatformSettings(cachedPlatformSettings);
      })
      .catch(() => {});
  }, []);

  // Fail closed while platformSettings hasn't loaded yet (null) - canAccessChallenge's own
  // `!platformSettings?.challengeInternalOnly` reads a not-yet-loaded config as "gate is off" and
  // shows the Challenge tab to everyone, including a gated-out user, until the real config arrives
  // a moment later and it disappears. Only treat it as allowed once we actually know the answer.
  const challengeAllowed = platformSettings !== null && canAccessChallenge(platformSettings, getStoredUserEmail());
  // The tab itself, not just where it leads: previously challengeAllowed alone decided whether the
  // tab showed, and a user with no active JEE Main pack would still see it, then hit the paywall
  // sheet (via handleChallengeClick below) only after tapping it. Gating visibility on pack
  // ownership too means a user who can never actually access the challenge doesn't see an entry
  // point to it at all - hasActiveJeeMainPack(purchasedPacks) fails closed (false) the same way
  // challengeAllowed does while purchasedPacks hasn't loaded yet, so no flash of a tab a non-owner
  // then loses.
  const showChallengeTab = challengeAllowed && hasActiveJeeMainPack(purchasedPacks);

  const handleChallengeClick = () => {
    if (hasActiveJeeMainPack(purchasedPacks)) {
      navigate('/challenge');
    } else {
      setShowJeeMainPaywall(true);
    }
  };

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => setIsMobile(window.innerWidth < 800);
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Detect minimal sidebar mode
  const isMinimalSidebar = false; //!purchasedPacks?.length;

  const navItems = useMemo(() => {
    if (isMinimalSidebar) {
      return [
        { name: 'Home', icon: Home, href: '/v2/home' },
        { name: 'All Tests', icon: ClipboardList, href: `/v3/${packId}/tests` },
        ...(showChallengeTab
          ? [{ name: 'Challenge', icon: Trophy, href: '/challenge', onClick: handleChallengeClick }]
          : []),
        { name: 'My Profile', icon: CircleUserRound, href: `/v3/profile` },
      ];
    }

    const baseItems = [
      { name: 'Home', icon: Home, href: '/v2/home' },
      { name: 'All Tests', icon: ClipboardList, href: `/v3/${packId}/tests` },
      ...(showChallengeTab
        ? [{ name: 'Challenge', icon: Trophy, href: '/challenge', onClick: handleChallengeClick }]
        : []),
      { name: 'Analysis', icon: PresentationChartIcon, href: '/v3/pack-analysis' },
    ];

    if (isMobile) {
      return [
        ...baseItems,
        { name: 'Mistakes', icon: AlertTriangle, href: `/v3/my-mistakes` },
        { name: 'Notebooks', icon: Bookmark, href: '/v3/notebooks' },
        { name: 'My Profile', icon: CircleUserRound, href: `/v3/profile` },
      ];
    } else {
      return [
        ...baseItems,
        { name: 'Mistakes', icon: AlertTriangle, href: `/v3/my-mistakes` },
        { name: 'Notebooks', icon: Bookmark, href: '/v3/notebooks' },
        { name: 'Resources', icon: Inbox, href: `/v3/${packId}/resources` },
        { name: 'My Profile', icon: CircleUserRound, href: `/v3/profile` },
      ];
    }
  }, [location.pathname, packId, isMobile, isMinimalSidebar, purchasedPacks, showChallengeTab]);

  // Hotkeys
  useEffect(() => {
    const handler = (event, hotkey) => {
      event.preventDefault();
      if (hotkey.key === 'shift+t') navigate(`/v3/${packId}/tests`);
      if (hotkey.key === 'shift+n') navigate('/v3/notebooks');
      if (hotkey.key === 'shift+m') navigate(`/v3/my-mistakes`);
      if (hotkey.key === 'shift+return') navigate('/v2/home');
      if (hotkey.key === 'shift+p') navigate('/v3/pack-analysis');
    };

    hotkeys('shift+t,shift+n,shift+m,shift+return,shift+p', handler);
    return () => {
      hotkeys.unbind('shift+t,shift+n,shift+m,shift+return,shift+p', handler);
    };
  }, [packId, navigate]);

  const handleNavigation = (item) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.href) {
      navigate(item.href);
    }
  };

  return (
    <>
      <JeeMainPaywallSheet open={showJeeMainPaywall} onClose={() => setShowJeeMainPaywall(false)} />
      {/* Desktop Sidebar */}
      <div
        className={
          !withOldComponent
            ? 'fixed left-0 top-0 w-[102px] h-screen bg-[#121212] text-white py-3 hidden lg:flex flex-col'
            : 'fixed left-0 top-0 w-[102px] h-screen bg-[#121212] text-white py-3 flex flex-col'
        }
      >
        <div className="flex mb-2 flex-col items-center ">
          <div className="mb-1 flex w-10 items-center justify-center">
            <img src={QuizrrLogo} alt="Quizrr Logo" className="w-8.5" />
          </div>
          <div className="text-center text-[10px] ">Quizrr</div>
        </div>
        <hr className="border-[#2A2A2A]" />

        {/* Navigation */}
        <div className="relative flex-1 flex flex-col overflow-y-auto">
          <nav className="flex flex-1 flex-col my-5 items-center">
            {navItems.map((item) => {
              const isActive = Boolean(item.href) && location.pathname.startsWith(item.href);
              // Same gold-gradient-plus-sweep treatment as the challenge page's own "99 Percentile
              // Challenge" pill (Challenge99Home.css's .c99h-dh-pill) - reusing the app's existing
              // animate-badge-shimmer sweep (see the "New" solution-tab badge) rather than a second,
              // bespoke keyframe, just with the pill's gold base instead of that badge's red one.
              const isChallenge = item.name === 'Challenge';
              return (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item)}
                  className={`flex w-full cursor-pointer flex-col items-center ${isChallenge ? 'text-[#2a1a05]' : isActive ? 'text-black' : 'text-white'}`}
                  disabled={!item.href && !item.onClick}
                >
                  <div
                    className={cn(
                      'mb-1 flex flex-col gap-2 h-18 w-19 items-center justify-center p-2 rounded-lg relative overflow-hidden',
                      isChallenge &&
                        'bg-gradient-to-r from-[#f7d98b] via-[#fbe3a3] to-[#e9b54e] after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:w-1/3 after:bg-gradient-to-r after:from-transparent after:via-white/70 after:to-transparent after:animate-badge-shimmer',
                      !isChallenge && isActive && 'bg-white',
                      !isChallenge && !isActive && 'transition-colors duration-200 hover:!bg-[#27333d]',
                    )}
                  >
                    <item.icon
                      size={20}
                      className={isChallenge ? 'text-[#2a1a05]' : isActive ? 'text-[black]' : 'text-[white]'}
                    />
                    <span className="text-xs ">{item.name}</span>
                  </div>
                </button>
              );
            })}
          </nav>
          <div className="sticky bottom-0 w-full h-[50px] pointer-events-none bg-gradient-to-t from-[#121212] to-transparent z-10" />
        </div>

        <div className="px-2 py-2 flex justify-center">
          <DarkModeToggle sidebar={true} />
        </div>

        {/* Test Series Switcher */}
        {purchasedFreePacksCollective?.length > 0 && (
          <div className="mt-auto border-t border-gray-800 pt-4">
            <div className="px-2 pb-2 text-center text-xs text-gray-400">Change Test Series</div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="mx-2 cursor-pointer  mb-4 rounded-md bg-white p-3">
                  <div className="flex flex-col items-center hover:scale-[1.02] duration-300">
                    <div className="mb-1">
                      <img src={selectedPack?.icon} alt="JEE Main" className="h-6 w-6" />
                    </div>
                    <div className="text-center text-xs font-medium text-black">
                      <div className="rounded-lg bg-white text-center text-sm cursor-pointer">
                        {selectedPack?.shortTitle || selectedPack?.exam?.title || selectedPack?.title}
                      </div>
                      <DropdownMenuContent className="rounded-xl bg-[var(--q3-surface-default)] shadow-lg w-[--radix-select-trigger-width] min-w-[--radix-select-trigger-width]">
                        {purchasedFreePacksCollective?.map((each) => {
                          const isSelected = selectedPack?._id === each._id;
                          return (
                            <DropdownMenuItem
                              key={each._id}
                              onSelect={() => onPackChange(each._id)}
                              className={`flex justify-between items-center px-4 py-4 font-semibold cursor-pointer w-full ${
                                isSelected
                                  ? 'bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)]'
                                  : 'bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)]'
                              }`}
                            >
                              <div className="flex items-center gap-2 w-full">
                                <div
                                  aria-label={`${each?.title} icon`}
                                  role="img"
                                  style={{
                                    backgroundImage: `url(${each?.icon})`,
                                    width: '24px',
                                    height: '24px',
                                    backgroundSize: 'contain',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'center',
                                  }}
                                />
                                <span className="font-medium" style={{ fontSize: '16px' }}>
                                  {each?.title}
                                </span>
                              </div>
                              {isSelected && <Check className="w-5 h-5 flex-shrink-0" />}
                            </DropdownMenuItem>
                          );
                        })}
                        <DropdownMenuItem
                          onSelect={() => window.open('https://www.quizrr.in/#packs', '_blank')}
                          className="px-4 py-4 cursor-pointer text-[var(--q3-neutral-default)] w-full"
                        >
                          <div className="flex items-center justify-between w-full gap-3">
                            <span className="text-sm font-medium text-[var(--q3-neutral-default)]">
                              View Other Test Series
                            </span>
                            <ExternalLink className="w-4 h-4 text-[var(--q3-neutral-default)] flex-shrink-0" />
                          </div>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </div>
                  </div>
                </div>
              </DropdownMenuTrigger>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      {!location.pathname.includes('resources') && !location.pathname.includes('analysis') && !isMinimalSidebar && (
        <div className="fixed bottom-0 left-0 right-0 z-50 block bg-[#121212] shadow-lg lg:hidden">
          <nav className="flex h-16 items-center justify-around">
            {navItems
              .filter((item) => item.name !== 'My Profile')
              .map((item) => {
                const isActive = Boolean(item.href) && location.pathname.startsWith(item.href);
                const isChallenge = item.name === 'Challenge';
                return (
                  <button
                    key={item.name}
                    onClick={() => handleNavigation(item)}
                    className={cn(
                      'relative flex cursor-pointer h-full w-full flex-col items-center justify-center',
                      isActive ? 'text-[var(--q3-accent-normal)]' : 'text-[var(--q3-neutral-light)]',
                    )}
                    disabled={!item.href && !item.onClick}
                  >
                    {isActive && <div className="absolute top-0 h-0.5 w-full bg-[var(--q3-accent-normal)]"></div>}
                    {isChallenge ? (
                      <span className="relative flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-[#f7d98b] via-[#fbe3a3] to-[#e9b54e] after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:w-1/3 after:bg-gradient-to-r after:from-transparent after:via-white/70 after:to-transparent after:animate-badge-shimmer">
                        <item.icon size={16} className="relative z-10 text-[#2a1a05]" />
                      </span>
                    ) : (
                      <item.icon size={20} />
                    )}
                    <span className="mt-1 text-xs">{item.name === 'All Tests' ? 'Tests' : item.name}</span>
                  </button>
                );
              })}
          </nav>
        </div>
      )}
    </>
  );
}
